import { detectCreditCard } from '@vend/credit-card-detector'
import { ApiCustomer } from '@vend/types'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  FulfilmentDetail,
  FulfilmentType,
  isDefinedFulfilmentType,
  isNoteTooLong,
} from '../../../../../models/fulfilment'
import { useVdFeatureManager } from '../../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../../state/hooks'
import {
  selectLineItem,
  selectSaleCustomer,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import {
  selectIsSaleCustomerContactInfoReviewed,
  selectOpenFulfilmentPopoverLineItemId,
} from '../../../../../state/slices/uiStateSlice'
import { PartialCustomerGroup } from '../../../../react/modals/common/types'
import { WALKIN_CUSTOMER_CODE } from '../../../sale-model.constant'
import {
  FEATURE_CAPTURE_ADDRESS_SELL_SCREEN,
  LRX_30788_OUT_OF_STOCK_FLOW,
} from '../../../../../constants'
import { isMissingContactInfo } from './LineItemFulfillmentUtils'
import { shouldFixFulfillmentAddresses } from './utils/fulfillmentHelper'

interface LineItemFulfillmentContextProps {
  currentView: CurrentView
  setCurrentView: React.Dispatch<React.SetStateAction<CurrentView>>
  currentDecision: ShouldFulfillOpt
  setCurrentDecision: React.Dispatch<React.SetStateAction<ShouldFulfillOpt>>
  currentCustomer: ApiCustomer | null
  setCurrentCustomer: React.Dispatch<React.SetStateAction<ApiCustomer | null>>
  originalCustomer: ApiCustomer | null
  setOriginalCustomer: React.Dispatch<React.SetStateAction<ApiCustomer | null>>
  setIsCurrentCustomerRemoved: React.Dispatch<React.SetStateAction<boolean>>
  customerGroup: PartialCustomerGroup | undefined
  setCustomerGroup: React.Dispatch<
    React.SetStateAction<PartialCustomerGroup | undefined>
  >
  isEmailInvalid: boolean
  setIsEmailInvalid: React.Dispatch<React.SetStateAction<boolean>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  fulfilmentDetails: FulfilmentDetail
  setFulfilmentDetails: React.Dispatch<React.SetStateAction<FulfilmentDetail>>
  isFulfilmentNoteInvalid: boolean
  onSelectContinueAnyway?: () => void
  onSelectFulfilmentType?: (type: FulfilmentType) => void
  isBreakdownSelected: boolean
  setIsBreakdownSelected: React.Dispatch<React.SetStateAction<boolean>>
}

export enum ShouldFulfillOpt {
  NO = 'no',
  YES = 'yes',
}

const LineItemFulfillmentContext = createContext<
  LineItemFulfillmentContextProps | undefined
>(undefined)

export enum CurrentView {
  DECISION = 'decision',
  ADD_CUSTOMER = 'add_customer',
  CREATE_NEW_CUSTOMER = 'create_new_customer',
  REVIEW_CONTACT_INFO = 'review_contact_info',
  FULFILMENT_METHOD = 'fulfilment_method',
  FIX_SALE_ADDRESSES = 'fix_sale_addresses',
}

export const LineItemFulfillmentProvider: React.FC<{
  children: ReactNode
  defaultView?: CurrentView
  defaultFulfillmentDetail?: FulfilmentDetail
  onSelectContinueAnyway?: () => void
  onSelectFulfilmentType?: (type: FulfilmentType) => void
}> = ({
  children,
  defaultView,
  defaultFulfillmentDetail = {
    type: FulfilmentType.UNSPECIFIED,
    note: '',
  },
  onSelectContinueAnyway,
  onSelectFulfilmentType,
}) => {
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const isContactInfoReviewed = useAppSelector(
    selectIsSaleCustomerContactInfoReviewed
  )
  const lineItemId = useAppSelector(selectOpenFulfilmentPopoverLineItemId)
  const lineItem = useAppSelector(selectLineItem(lineItemId))

  const [currentDecision, setCurrentDecision] = useState<ShouldFulfillOpt>(
    ShouldFulfillOpt.NO
  )
  const [isEmailInvalid, setIsEmailInvalid] = useState(false)
  const [customerGroup, setCustomerGroup] = useState<
    PartialCustomerGroup | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [fulfilmentDetails, setFulfilmentDetails] = useState<FulfilmentDetail>(
    defaultFulfillmentDetail
  )

  const lifBugFixesEnabled = useVdFeatureManager().get(
    'fulfillment_lif_bug_fixes'
  )

  const isCaptureAddressSellScreenEnabled = useVdFeatureManager().get(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )

  const isOutOfStockFlowFeatureEnabled = useVdFeatureManager().get(
    LRX_30788_OUT_OF_STOCK_FLOW
  )

  const needToFixSaleAddresses = shouldFixFulfillmentAddresses(
    fulfilmentDetails
  )

  // Product Packaging Breakdown
  const [isBreakdownSelected, setIsBreakdownSelected] = useState(false)

  // Setup customer
  let initialCurrentCustomer = null
  let initialOriginalCustomer = null
  const isWalkinCustomer = saleCustomer?.customer_code === WALKIN_CUSTOMER_CODE

  if (lifBugFixesEnabled && !isWalkinCustomer) {
    initialCurrentCustomer = saleCustomer
    initialOriginalCustomer = saleCustomer
  }

  const [currentCustomer, setCurrentCustomer] = useState<ApiCustomer | null>(
    initialCurrentCustomer
  )
  const [originalCustomer, setOriginalCustomer] = useState<ApiCustomer | null>(
    initialOriginalCustomer
  )
  const [
    isCurrentCustomerRemoved,
    setIsCurrentCustomerRemoved,
  ] = useState<boolean>(false)

  // Setup view
  let initialView = defaultView || CurrentView.DECISION

  if (
    lifBugFixesEnabled &&
    !defaultView &&
    isDefinedFulfilmentType(lineItem?.fulfilmentType)
  ) {
    if (currentCustomer) {
      if (isMissingContactInfo(currentCustomer) || !isContactInfoReviewed) {
        initialView = CurrentView.REVIEW_CONTACT_INFO
      } else if (
        needToFixSaleAddresses &&
        lineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
        isCaptureAddressSellScreenEnabled
      ) {
        initialView = CurrentView.FIX_SALE_ADDRESSES
      } else {
        initialView = CurrentView.FULFILMENT_METHOD
      }
    } else {
      initialView = CurrentView.ADD_CUSTOMER
    }
  }

  // Adjust initial view for delivery fulfilment type when defaultView is provided
  if (
    defaultView &&
    lineItem?.fulfilmentType === FulfilmentType.DELIVERY &&
    fulfilmentDetails.type === FulfilmentType.DELIVERY &&
    needToFixSaleAddresses &&
    isCaptureAddressSellScreenEnabled &&
    isOutOfStockFlowFeatureEnabled
  ) {
    initialView = CurrentView.FIX_SALE_ADDRESSES
  }

  const [currentView, setCurrentView] = useState<CurrentView>(initialView)

  useEffect(() => {
    if (
      saleCustomer != null &&
      currentCustomer == null &&
      currentView === CurrentView.ADD_CUSTOMER &&
      isCurrentCustomerRemoved === false &&
      saleCustomer?.customer_code !== WALKIN_CUSTOMER_CODE
    ) {
      setOriginalCustomer(saleCustomer)
      setCurrentCustomer(saleCustomer)
      setCurrentView(CurrentView.REVIEW_CONTACT_INFO)
    }
  }, [currentView, saleCustomer, currentCustomer, isCurrentCustomerRemoved])

  useEffect(() => {
    if (
      !lifBugFixesEnabled &&
      saleCustomer?.customer_code !== WALKIN_CUSTOMER_CODE
    ) {
      setOriginalCustomer(saleCustomer)
      setCurrentCustomer(saleCustomer)
    }
  }, [lifBugFixesEnabled, saleCustomer])

  useEffect(() => {
    if (
      !lifBugFixesEnabled &&
      !defaultView &&
      isDefinedFulfilmentType(lineItem?.fulfilmentType)
    ) {
      if (currentCustomer) {
        if (isMissingContactInfo(currentCustomer) || !isContactInfoReviewed) {
          setCurrentView(CurrentView.REVIEW_CONTACT_INFO)
        } else {
          if (needToFixSaleAddresses) {
            setCurrentView(CurrentView.FIX_SALE_ADDRESSES)
          } else {
            setCurrentView(CurrentView.FULFILMENT_METHOD)
          }
        }
      } else {
        setCurrentView(CurrentView.ADD_CUSTOMER)
      }
    }
  }, [
    lifBugFixesEnabled,
    defaultView,
    lineItem,
    currentCustomer,
    isContactInfoReviewed,
    needToFixSaleAddresses,
  ])

  return (
    <LineItemFulfillmentContext.Provider
      value={{
        currentDecision,
        setCurrentDecision,
        currentView,
        setCurrentView,
        isEmailInvalid,
        setIsEmailInvalid,
        currentCustomer,
        setCurrentCustomer,
        originalCustomer,
        setOriginalCustomer,
        setIsCurrentCustomerRemoved,
        customerGroup,
        setCustomerGroup,
        isLoading,
        setIsLoading,
        fulfilmentDetails,
        setFulfilmentDetails,
        onSelectContinueAnyway,
        onSelectFulfilmentType,
        isFulfilmentNoteInvalid:
          detectCreditCard(fulfilmentDetails.note) ||
          isNoteTooLong(fulfilmentDetails.note),
        isBreakdownSelected,
        setIsBreakdownSelected,
      }}
    >
      {children}
    </LineItemFulfillmentContext.Provider>
  )
}

export const useLineItemFulfillmentContext = (): LineItemFulfillmentContextProps => {
  const context = useContext(LineItemFulfillmentContext)
  if (!context) {
    throw new Error(
      'useLineItemFulfillment must be used within a LineItemFulfillmentProvider'
    )
  }
  return context
}
